/**
 * CR325: Stability Mode hook
 *
 * When stability mode is ON:
 * - Background refetch / polling is disabled
 * - Auto-hydrate re-runs after initial load are blocked
 * - Refetch-on-focus / visibility is suppressed
 * - A manual "Refresh" action triggers one controlled hydrate
 *
 * Default: ON (until bounce root cause is fixed).
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { logEvt } from "@/lib/debug/dealCardEditorEvents";

export interface StabilityModeResult {
  /** Whether stability mode is active */
  stabilityMode: boolean;
  /** Toggle stability mode on/off */
  toggleStabilityMode: () => void;
  /** Set stability mode explicitly */
  setStabilityMode: (on: boolean) => void;
  /**
   * Whether a refetch/hydration is allowed right now.
   * When stability mode is ON, returns false for background triggers
   * but returns true for manual refresh.
   */
  isRefetchAllowed: (trigger: "background" | "manual" | "initial") => boolean;
  /** Increment manual refresh counter — call this when user clicks Refresh */
  triggerManualRefresh: () => number;
  /** Current manual refresh count (for dependency tracking) */
  manualRefreshCount: number;
}

const STORAGE_KEY = "dc_stability_mode";

export function useStabilityMode(defaultOn = true): StabilityModeResult {
  const [stabilityMode, setStabilityModeState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored !== null) return stored === "1";
    } catch { /* noop */ }
    return defaultOn;
  });

  const [manualRefreshCount, setManualRefreshCount] = useState(0);
  const initialLoadDone = useRef(false);

  // Persist preference
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, stabilityMode ? "1" : "0");
    } catch { /* noop */ }
  }, [stabilityMode]);

  const toggleStabilityMode = useCallback(() => {
    setStabilityModeState(prev => {
      const next = !prev;
      logEvt({ type: next ? "stability_mode_on" : "stability_mode_off" });
      return next;
    });
  }, []);

  const setStabilityMode = useCallback((on: boolean) => {
    setStabilityModeState(on);
    logEvt({ type: on ? "stability_mode_on" : "stability_mode_off" });
  }, []);

  const isRefetchAllowed = useCallback(
    (trigger: "background" | "manual" | "initial"): boolean => {
      if (trigger === "manual") return true;
      if (trigger === "initial") {
        if (!initialLoadDone.current) {
          initialLoadDone.current = true;
          return true;
        }
        // After initial load, treat as background
        return !stabilityMode;
      }
      // Background: blocked when stability mode is on
      return !stabilityMode;
    },
    [stabilityMode]
  );

  const triggerManualRefresh = useCallback((): number => {
    logEvt({ type: "manual_refresh" });
    const next = manualRefreshCount + 1;
    setManualRefreshCount(next);
    return next;
  }, [manualRefreshCount]);

  return {
    stabilityMode,
    toggleStabilityMode,
    setStabilityMode,
    isRefetchAllowed,
    triggerManualRefresh,
    manualRefreshCount,
  };
}
